import { makeAction, makeReducer } from '@finn-no/redux-actions';
import Router from 'next/router';
import { getApiUrl } from '@/api/apiUrl';
import { fetchOffersForHotel } from '@/api/fetcher';
import { ApiActionStatus, type HotelOffersResponse, type ThunkResult } from '@/interfaces';
import { filtersToApiQueryParams, filtersToBrowserQueryParams } from '@/lib/utils/queryParamFilterConversions';
import { stringifyParams } from '@/lib/utils/queryString';
import defaultState from './defaultState';

export const actions = {
    setPending: makeAction<HotelOffersResponse>('HOTEL_RESULT_PENDING', (state) => ({
        ...state,
        status: ApiActionStatus.PENDING,
    })),
    setResults: makeAction<HotelOffersResponse>('HOTEL_RESULT_OK', (_, action) => ({
        ...defaultState.hotelResults,
        ...action.payload,
        status: ApiActionStatus.OK,
    })),
    setError: makeAction<HotelOffersResponse>('HOTEL_RESULT_ERROR', (state) => ({
        ...state,
        status: ApiActionStatus.ERROR,
    })),
    setNotModified: makeAction<HotelOffersResponse>('HOTEL_RESULT_NOT_MODIFIED', (state) => ({
        ...state,
        status: ApiActionStatus.OK,
    })),
};

export const fetchHotelResults =
    // biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
        (payload?: any): ThunkResult<Promise<void>> =>
        async (dispatch, getState): Promise<void> => {
            dispatch(actions.setPending());
            const queryParams = stringifyParams(filtersToBrowserQueryParams(getState().filters));
            await Router.replace({ pathname: Router.pathname, query: queryParams }, undefined, { shallow: true });
            try {
                const data = await fetchOffersForHotel(getApiUrl(), filtersToApiQueryParams({ ...getState().filters, ...payload }));
                if (!data) {
                    dispatch(actions.setNotModified());
                    return;
                }
                dispatch(actions.setResults(data));
            } catch {
                dispatch(actions.setError());
            }
        };

export const fetchHotelResultsToAppend =
    // biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
        (payload?: any): ThunkResult<Promise<void>> =>
        async (dispatch, getState): Promise<void> => {
            dispatch(actions.setPending());
            const topOffers = getState().hotelResults.offers;
            try {
                const data = await fetchOffersForHotel(getApiUrl(), filtersToApiQueryParams({ ...getState().filters, ...payload }));
                if (!data) {
                    dispatch(actions.setNotModified());
                    return;
                }
                const { offers, ...newHotelResults } = data;
                // Append the new offers to the previous (top) offers.
                const appendedOffers = [...topOffers, ...offers];
                dispatch(actions.setResults({ ...newHotelResults, offers: appendedOffers }));
            } catch {
                dispatch(actions.setError());
            }
        };

export default actions;
export const hotelResultReducer = makeReducer<HotelOffersResponse>(actions);
